<template>
  <div class="page" v-if="form">
    <h2>{{ locked ? '查看表单' : '编辑表单' }} · {{ form.title }}</h2>
    <p v-if="locked" class="lock-note">
      已有 {{ form.submissionCount }} 条提交记录，不能修改表单。
      <template v-if="form.status !== 'CLOSED'">可以关闭，关闭后不再接受新的提交。</template>
    </p>
    <button v-if="canClose" type="button" class="btn warn" @click="closeForm">关闭表单</button>
    <p>分享链接：<code>{{ shareUrl }}</code> <button type="button" @click="copyLink">复制</button></p>

    <section class="card">
      <h3>基本信息</h3>
      <div class="grid">
        <label>Code<input v-model="form.code" :disabled="locked" /></label>
        <label>标题<input v-model="form.title" :disabled="locked" /></label>
        <label>状态
          <select v-model="form.status" :disabled="locked">
            <option v-for="s in statusOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
          </select>
        </label>
        <label>每人最多提交<input v-model.number="form.maxSubmissionsPerUser" type="number" min="1" max="3" :disabled="locked" /></label>
        <label class="full">说明<textarea v-model="form.description" rows="3" :disabled="locked" /></label>
        <label>截止时间<input v-model="endAtLocal" type="datetime-local" :disabled="locked" /></label>
      </div>
      <button v-if="!locked" class="btn primary" @click="saveMeta">保存基本信息</button>
    </section>

    <section class="card">
      <h3>字段（{{ fields.length }}）</h3>
      <div v-for="(field, idx) in fields" :key="idx" class="field-row">
        <input v-model="field.fieldKey" placeholder="fieldKey" :disabled="locked" />
        <input v-model="field.label" placeholder="题目" class="grow" :disabled="locked" />
        <select v-model="field.type" :disabled="locked">
          <option v-for="t in fieldTypes" :key="t.value" :value="t.value">{{ t.label }}</option>
        </select>
        <label><input type="checkbox" v-model="field.required" :disabled="locked" /> 必填</label>
        <label><input type="checkbox" v-model="field.sensitive" :disabled="locked" /> 敏感</label>
        <input v-model="optionsText[idx]" placeholder="选项（逗号分隔，单选/多选）" class="grow" :disabled="locked" />
      </div>
      <button v-if="!locked" type="button" @click="addField">添加字段</button>
      <button v-if="!locked" class="btn primary" @click="saveFields">保存字段</button>
    </section>
  </div>
</template>

<script>
import { formService } from '@/services/formService'

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: '草稿' },
  { value: 'PUBLISHED', label: '已发布' },
  { value: 'CLOSED', label: '已关闭' }
]

const FIELD_TYPES = [
  { value: 'TEXT', label: '单行文本' },
  { value: 'TEXTAREA', label: '多行文本' },
  { value: 'RADIO', label: '单选' },
  { value: 'CHECKBOX', label: '多选' },
  { value: 'EMAIL', label: '邮箱' },
  { value: 'PHONE', label: '手机号' },
  { value: 'NUMBER', label: '数字' }
]

export default {
  name: 'FormEditor',
  data() {
    return {
      form: null,
      fields: [],
      optionsText: [],
      fieldTypes: FIELD_TYPES,
      statusOptions: STATUS_OPTIONS
    }
  },
  computed: {
    shareUrl() {
      return this.form ? formService.shareUrl(this.form.code) : ''
    },
    locked() {
      return (this.form?.submissionCount || 0) > 0
    },
    canClose() {
      return this.locked && this.form?.status !== 'CLOSED'
    },
    endAtLocal: {
      get() {
        if (!this.form?.endAt) return ''
        return String(this.form.endAt).slice(0, 16)
      },
      set(v) {
        this.form.endAt = v ? `${v}:00` : null
      }
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      const id = this.$route.params.id
      const res = await formService.getForm(id)
      if (res.success) {
        this.form = { ...res.data }
        this.fields = (res.data.fields || []).map(f => ({ ...f }))
        this.optionsText = this.fields.map(f => (f.options || []).join(','))
      }
    },
    addField() {
      this.fields.push({
        sortOrder: this.fields.length + 1,
        fieldKey: `q${this.fields.length + 1}`,
        label: '新题目',
        type: 'TEXT',
        required: false,
        sensitive: false
      })
      this.optionsText.push('')
    },
    async closeForm() {
      if (!confirm('关闭后不再接受新的提交，已有记录会保留。确定关闭？')) return
      try {
        const res = await formService.updateForm(this.form.id, {
          code: this.form.code,
          title: this.form.title,
          description: this.form.description,
          status: 'CLOSED',
          startAt: this.form.startAt,
          endAt: this.form.endAt,
          maxSubmissionsPerUser: this.form.maxSubmissionsPerUser
        })
        if (res.success) await this.load()
        else alert(res.error || '关闭失败')
      } catch (e) {
        alert(e.response?.data?.error || '关闭失败')
      }
    },
    async saveMeta() {
      const payload = {
        code: this.form.code,
        title: this.form.title,
        description: this.form.description,
        status: this.form.status,
        startAt: this.form.startAt,
        endAt: this.form.endAt,
        maxSubmissionsPerUser: this.form.maxSubmissionsPerUser
      }
      const res = await formService.updateForm(this.form.id, payload)
      if (res.success) alert('已保存')
      else alert(res.error || '保存失败')
    },
    async saveFields() {
      const body = this.fields.map((f, i) => ({
        id: f.id,
        sortOrder: i + 1,
        fieldKey: f.fieldKey,
        label: f.label,
        type: f.type,
        required: f.required,
        placeholder: f.placeholder,
        helpText: f.helpText,
        sensitive: f.sensitive,
        requiredWhen: f.requiredWhen,
        options: this.optionsText[i] ? this.optionsText[i].split(/[,，]/).map(s => s.trim()).filter(Boolean) : null
      }))
      const res = await formService.saveFields(this.form.id, body)
      if (res.success) {
        alert('字段已保存')
        await this.load()
      } else alert(res.error || '保存失败')
    },
    copyLink() {
      navigator.clipboard.writeText(this.shareUrl).then(() => alert('已复制'))
    }
  }
}
</script>

<style scoped>
.page { padding: 24px; }
.card { background: #fafafa; padding: 16px; margin-top: 16px; border-radius: 8px; }
.grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.grid label { display: flex; flex-direction: column; gap: 4px; font-size: 13px; }
.grid .full { grid-column: 1 / -1; }
.field-row { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; align-items: center; }
.grow { flex: 1; min-width: 160px; }
.page select {
  box-sizing: border-box;
  height: 40px;
  min-height: 40px;
  max-height: 40px;
  margin: 0;
  padding: 0 32px 0 12px;
  font-size: 14px;
  line-height: 40px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23666' d='M1.4.6 6 5.2 10.6.6 12 2 6 8 0 2z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 12px 8px;
}
.btn.primary { margin-top: 12px; background: #409eff; color: #fff; border: none; padding: 8px 16px; border-radius: 4px; }
.lock-note { color: #ad6800; background: #fff7e6; border: 1px solid #ffd591; padding: 8px 12px; border-radius: 4px; }
.btn.warn { margin-top: 12px; background: #fff; color: #ad6800; border: 1px solid #ffd591; padding: 8px 16px; border-radius: 4px; cursor: pointer; }
input:disabled, select:disabled, textarea:disabled { background: #f5f5f5; color: #666; }
</style>
